import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Send, Trash2, RefreshCw, Shield } from "lucide-react";

/**
 * Admin Settings card — invite board directors and manage the ones already
 * seated. Directors get read-only access to just /board/finance.
 */
export function BoardMembersCard() {
  const { properties } = useProperty();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role_label: "Director", property_id: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/board/members");
      setMembers(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not load board members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) return toast.error("Email and name are required");
    setInviting(true);
    try {
      const body = {
        email: form.email.toLowerCase(),
        name: form.name,
        role_label: form.role_label || "Director",
        property_ids: form.property_id === "all" ? [] : [form.property_id],
      };
      const { data } = await api.post("/admin/board/invite", body);
      toast.success(data.action === "updated" ? "Board seat updated" : "Invite sent");
      setForm({ email: "", name: "", role_label: "Director", property_id: "all" });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const resend = async (email) => {
    try {
      await api.post("/admin/board/resend-invite", { email });
      toast.success("Invite resent");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not resend");
    }
  };

  const revoke = async (m) => {
    if (!window.confirm(`Revoke board access for ${m.name} (${m.email})?`)) return;
    try {
      await api.delete(`/admin/board/members/${m.id}`);
      toast.success("Access revoked");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Revoke failed");
    }
  };

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="board-members-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Governance</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-400" /> Board of directors
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            Read-only access to the financial dashboard. Directors log in via <code className="text-[11px]">/login</code>, they never see operations pages.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10" data-testid="board-members-refresh">
          <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      <form onSubmit={invite} className="rounded-md border border-white/10 bg-white/[0.02] p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="md:col-span-1">
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
          <Input data-testid="board-invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="mt-1 bg-white/[0.04] border-white/10 text-white" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
          <Input data-testid="board-invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" className="mt-1 bg-white/[0.04] border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Title</Label>
          <Select value={form.role_label} onValueChange={(v) => setForm({ ...form, role_label: v })}>
            <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="board-invite-title">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              <SelectItem value="President">President</SelectItem>
              <SelectItem value="Vice President">Vice President</SelectItem>
              <SelectItem value="Treasurer">Treasurer</SelectItem>
              <SelectItem value="Secretary">Secretary</SelectItem>
              <SelectItem value="Director">Director</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Community</Label>
          <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
            <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="board-invite-community">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              <SelectItem value="all">All communities</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-5 flex justify-end">
          <Button type="submit" disabled={inviting || !form.email || !form.name} className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="board-invite-submit">
            <Send className="h-3.5 w-3.5 mr-1" />
            {inviting ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </form>

      <div className="space-y-1">
        {members.length === 0 && (
          <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="board-empty">
            No directors seated yet. Send your first invite above.
          </div>
        )}
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 text-xs border border-white/5 rounded-md px-3 py-2 bg-white/[0.02]" data-testid={`board-member-${m.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-8 w-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center shrink-0">
                <Shield className="h-3.5 w-3.5 text-indigo-300" />
              </div>
              <div className="min-w-0">
                <div className="text-zinc-200 flex items-center gap-2 truncate">
                  {m.name}
                  <Badge variant="outline" className="border-indigo-500/40 text-indigo-200 bg-indigo-500/10 text-[9px]">{m.role_label || "Director"}</Badge>
                  {m.email_verified === false && (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 bg-amber-500/10 text-[9px]">pending set-password</Badge>
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 truncate">{m.email} · {(m.property_ids || []).length} {(m.property_ids || []).length === 1 ? "community" : "communities"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => resend(m.email)} className="h-7 px-2 text-orange-300 hover:text-orange-200 hover:bg-white/[0.05]" data-testid={`board-resend-${m.id}`}>
                Resend
              </Button>
              <Button size="sm" variant="ghost" onClick={() => revoke(m)} className="h-7 px-2 text-red-300 hover:text-red-200 hover:bg-white/[0.05]" data-testid={`board-revoke-${m.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
